import { zodResolver } from '@hookform/resolvers/zod';
import * as Dialog from '@radix-ui/react-dialog';
import { Dispatch, SetStateAction, Suspense, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Await, defer, useLoaderData } from 'react-router-dom';
import {
  Button,
  H1,
  H3,
  Input,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableLoading,
  TableRow,
} from 'ui-rollup';
import { z } from 'zod';
import { User } from '../../types/user';
import { fetchData } from '../../utils/fetchData';
import { getJwt } from '../../utils/getJwt';
import styles from './clients.module.css';

export const loader = () => {
  const clients = fetchData<User[]>(
    `${process.env.REACT_APP_BACKEND_API}/users/${getJwt().id}/clients`,
    {
      headers: {
        Authorization: `Bearer ${sessionStorage.getItem('token')}`,
      },
    },
  );

  return defer({ clients });
};

export const Clients = () => {
  const data = useLoaderData() as { clients: User[] };
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <AddClient open={isOpen} onOpenChange={setIsOpen} />
      <div className={styles.createContainer}>
        <H1>Clients 🤝</H1>
        <Button onClick={() => setIsOpen(true)}>Add Client</Button>
      </div>
      <div className={styles.tableContainer}>
        <Table>
          <TableRow>
            <TableHead># No.</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Company</TableHead>
            <TableHead>GST</TableHead>
            <TableHead />
          </TableRow>
          <TableBody>
            <Suspense fallback={<TableLoading columnCount={4} />}>
              <Await resolve={data.clients}>
                {(clients: User[]) =>
                  clients.map((client, index) => (
                    <TableRow key={client.id}>
                      <TableData>{index + 1}</TableData>
                      <TableData>{client.name}</TableData>
                      <TableData>{client.companyName}</TableData>
                      <TableData>{client.gst}</TableData>
                      {/* TODO: Add edit and delete functionality */}
                      <TableData>
                        <button>E</button>
                        <button>D</button>
                      </TableData>
                    </TableRow>
                  ))
                }
              </Await>
            </Suspense>
          </TableBody>
        </Table>
      </div>
    </>
  );
};

const AddClient = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}) => {
  const schema = z.object({
    name: z.string().min(1),
    email: z.string().email(),
    phone: z.string().length(10),
    companyName: z.string().min(1),
    gst: z.string().length(15), // TODO: Add regex validation for GST
    address: z.string().min(5).max(100),
  });
  type Customer = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Customer>({
    resolver: zodResolver(schema),
  });

  const handleCustomer: SubmitHandler<Customer> = async (data) => {
    // e.preventDefault();
    const response = await fetchData<any>(
      `${process.env.REACT_APP_BACKEND_API}/users/${getJwt().id}/clients`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
    console.log(response);
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.DialogOverlay} />
        <Dialog.Content className={styles.DialogContent}>
          <Dialog.Title className={styles.DialogTitle}>
            <H3>New Client 💼</H3>
          </Dialog.Title>
          <form
            className={styles.formContainer}
            onSubmit={handleSubmit(handleCustomer)}
          >
            <Input
              label="Name"
              placeholder="Enter here"
              errorMessage={errors.name?.message}
              {...register('name')}
            />
            <Input
              label="Email"
              placeholder="Enter here"
              type="email"
              errorMessage={errors.email?.message}
              {...register('email')}
            />
            <Input
              label="Phone"
              placeholder="Enter here"
              type="tel"
              errorMessage={errors.phone?.message}
              {...register('phone')}
            />
            <Input
              label="Company"
              placeholder="Enter here"
              errorMessage={errors.companyName?.message}
              {...register('companyName')}
            />
            <Input
              label="GST"
              placeholder="Enter here"
              errorMessage={errors.gst?.message}
              {...register('gst')}
            />
            <Input
              label="Address"
              placeholder="Enter here"
              errorMessage={errors.address?.message}
              {...register('address')}
            />
            <Button type="submit">Submit</Button>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
